import React, { useState, useMemo, } from 'react';
import { pick, sortBy, keyBy, isEmpty, uniq, omit, orderBy, get, groupBy, isEqual, isPlainObject } from 'lodash';
import { Button, Input, } from 'reactstrap';
import { format as formatDate, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, addDays, addMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import Select from 'react-select';
import { toast } from 'react-toastify';
import numeral from 'numeral';
import { useToggle, useCounter, useList, } from 'react-use';
import qs from 'qs';

import firebase from '../../firebase';
import { fullPathWithParams } from '../../util';
import { adminFields, statuses } from '../../shared/models/surveyAnswer';
import { fields as surveyAnswerUserVoiceFields } from '../../shared/models/surveyAnswerUserVoice';
import { areaFromPostalCode } from '../../shared/models/setting';
import { auditData } from '../../shared/models/user';
import { defaultGroups } from '../../shared/models/surveyGroup';
import AdminPage from '../hocs/AdminPage';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useQueryParams from '../hooks/useQueryParams';
import ExportButton from '../ExportButton';
import ModelFormModal from '../modals/ModelFormModal';
import QuerySelector from '../QuerySelector';
import QueryInput from '../QueryInput';
import QueryBoolean from '../QueryBoolean';
import EditButton from '../EditButton';
import AddButton from '../AddButton';
import DeleteButton from '../DeleteButton';
import TenantLink from '../TenantLink';
import Field from '../Field';
import DeleteSurveyAnswersButton from '../DeleteSurveyAnswersButton';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import AnswerStatModal from '../modals/AnswerStatModal';
import SavedSearchParameterForm from '../forms/SavedSearchParameterForm';
import BulkEmailButton from '../BulkEmailButton';
import BulkAdminNoteButton from '../BulkAdminNoteButton';

const { keys } = Object;
const db = firebase.firestore();
const settingsRef = db.collection('settings');
const surveyGroupsRef = db.collection('surveyGroups');

export default AdminPage(function AdminSurveyAnswers(props) {
  const { tenant, user, history, location, } = props;
  const [version, { inc: updateVersion }] = useCounter(0);
  const now = new Date();
  const queryParams = useQueryParams();
  const {
    surveys: surveysForFilter,
    groups: groupsForFilter,
    picNames: picNamesForFilter,
    statuses: statusesForFilter,
    areas: areasForFilter,
    excludeReinquiry: _excludeReinquiry = '0',
    dateRange,
    filterQuestionValue,
    filterQuestionText,
    filterQuestionHasImage,
    showsUserVoice: _showsUserVoice = '0',
    surveyAnswerId: surveyAnswerIdForFilter,
  } = queryParams;
  const filterQuestionId = queryParams.filterQuestionId?.[0];
  const [answerStatQuestionId, setAnswerStatQuestionId] = useState();
  const [checkedAnswers, setCheckedAnswers] = useState([]);
  const [_showsOnlyChecked, toggleShowsOnlyChecked] = useToggle(false);
  const showsOnlyChecked = _showsOnlyChecked && !isEmpty(checkedAnswers);
  const [showsAnswerStat, toggleAnswerStat] = useToggle();
  const startOn = dateRange?.[0] ? new Date(dateRange[0]) : startOfMonth(new Date());
  const endOn = dateRange?.[1] ? new Date(dateRange[1]) : endOfMonth(new Date());
  const excludeReinquiry = _excludeReinquiry === '1';
  const showsUserVoice = _showsUserVoice === '1';
  const userVoices = sortBy(useCollectionSubscriptionInTenant(db.collection('userVoices')), _ => _.createdAt.toDate());
  const userVoicesById = keyBy(userVoices, 'id');
  const _surveys = useCollectionSubscriptionInTenant(db.collection('surveys'));
  const surveys = useMemo(_ => orderBy(_surveys, (_) => _.createdAt.toDate(), 'desc'), [_surveys]);
  const maxStepsCount = Math.max(...surveys.map(_ => _.steps?.length || 1), 1);
  const stepOptions = Array(maxStepsCount).fill().map((_, i) => ({ label: `ステップ${i + 1}`, value: i.toString() }));
  const areaSetting = useDocumentSubscription(db.collection('settings').doc([tenant.id, 'area'].join('__')));
  const surveysById = useMemo(_ => keyBy(surveys, 'id'), [surveys]);
  const surveyOptions = sortBy(surveys, _ => !!_.isHidden)
    .filter(_ => isEmpty(groupsForFilter) || groupsForFilter.includes(_.surveyGroupId))
    .map((_) => ({
      label: _.isHidden ? (
        <div className="text-muted">
          [非表示] {_.name}
        </div>
      ) : _.name,
      value: _.id,
    }));
  const surveyAnswers = useCollectionSubscriptionInTenant(startOn && endOn && db.collection('surveyAnswers').where('createdAt', '>=', startOfDay(startOn)).where('createdAt', '<=', endOfDay(endOn)).orderBy('createdAt', 'desc'), [startOn?.toString(), endOn?.toString()]);
  const staffs = useCollectionSubscriptionInTenant(db.collection('users').where('role', 'in', ['admin', 'staff']));
  const staffsById = keyBy(staffs, 'uid');
  const tenantUsers = useDocumentsFetch(
    uniq(surveyAnswers.map(_ => _.createdBy?.uid).filter(_ => _)).map(uid => tenant?.ref.collection('tenantUsers').doc(uid)), 
    [surveyAnswers.map(_ => _.createdBy?.uid).join(','), tenant?.id]
  );
  const tenantUsersByUid = keyBy(tenantUsers, 'uid');
  
  const _questions = useCollectionSubscriptionInTenant(db.collection('questions'));
  const questions = useMemo(_ => sortBy(_questions), [_questions]);
  const surveyAnswerUserVoices = useCollectionsFetch(
    uniq(surveyAnswers.map(_ => _.id)).map(surveyAnswerId =>
      db.collection('surveyAnswerUserVoices').where('surveyAnswerId', '==', surveyAnswerId)
    ),
    [surveyAnswers.map(_ => _.id).join(','), version]
  ).flat();
  const groupedSurveyAnswerUserVoices = useMemo(_ =>
    groupBy(surveyAnswerUserVoices, _ => `${_.surveyAnswerId}__${_.questionId}`),
    [surveyAnswerUserVoices]
  );
  const rows = useMemo(_ => surveyAnswers.map((surveyAnswer) => {
    const survey = surveysById[surveyAnswer.surveyId];
    const area = areaFromPostalCode(surveyAnswer.createdBy.postalCode || '', areaSetting);
    const picName = surveyAnswer.respondedBy?.displayName;
    const tenantUser = tenantUsersByUid[surveyAnswer.createdBy?.uid];
    const accountAssignee = staffsById[tenantUser?.assigneeId];
    let filteredQuestionRows = survey?.questionRows;
    return {
      ...surveyAnswer,
      survey,
      area,
      picName,
      accountAssignee,
    };
  }), [surveyAnswers, surveysById, areaSetting, tenantUsersByUid, staffsById]);

  // アンケート項目の絞り込み用
  const selectedQuestion = questions.find(_ => _.id === filterQuestionId);
  console.log(questions, selectedQuestion, filterQuestionId);

  // NOTE: filter
  const filteredRows = useMemo(_ => {
    let filteredRows = rows;
    if (!isEmpty(surveysForFilter)) {
      filteredRows = filteredRows.filter((_) => surveysForFilter.includes(_.surveyId));
    }
    if (!isEmpty(groupsForFilter)) {
      filteredRows = filteredRows.filter((_) => groupsForFilter.includes(get(surveysById[_.surveyId], 'surveyGroupId')));
    }
    if (!isEmpty(picNamesForFilter)) {
      filteredRows = filteredRows.filter((_) => picNamesForFilter?.includes(_.picName));
    }
    if (!isEmpty(statusesForFilter)) {
      filteredRows = filteredRows.filter((_) => statusesForFilter?.includes(_.status));
    }
    if (!isEmpty(areasForFilter)) {
      filteredRows = filteredRows.filter((_) => areasForFilter.includes(_.area?.group));
    }
    
    // アンケート項目での絞り込み
    if (filterQuestionId && selectedQuestion) {
      filteredRows = filteredRows.filter(row => {
        const answer = row.answers[filterQuestionId];

        // 空の回答を除外
        if (!answer || (selectedQuestion.type === 'checkbox' && isEmpty(answer))) {
          return false;
        }

        if (selectedQuestion.type === 'text' && filterQuestionText) {
          return answer.includes(filterQuestionText);
        }

        if (selectedQuestion.type === 'imageFile' && filterQuestionHasImage !== undefined) {
          const hasImage = [answer].flat().some(_ => _?.url);
          return filterQuestionHasImage === '1' ? hasImage : !hasImage;
        }

        if (['checkbox', 'radio', 'select'].includes(selectedQuestion.type) && !isEmpty(filterQuestionValue)) {
          if (selectedQuestion.type === 'checkbox') {
            return filterQuestionValue.some(value => keys(answer).includes(value));
          } else {
            return filterQuestionValue.includes(answer);
          }
        }

        return true;
      });
    }
    
    if (excludeReinquiry) {
      filteredRows = filteredRows.filter((_) => !_.sourceTroubleInquiryId && !_.sourceInquiryId && !_.sourceSurveyAnswerId);
    }

    if (surveyAnswerIdForFilter) {
      filteredRows = filteredRows.filter((_) => _.id === surveyAnswerIdForFilter);
    }

    if (showsOnlyChecked) {
      filteredRows = filteredRows.filter((_) => checkedAnswers.includes(_.id));
    }

    return filteredRows;
  }, [rows, surveysById, queryParams, selectedQuestion, showsOnlyChecked, checkedAnswers]);

  const answeredSurveys = uniq(filteredRows.map(_ => _.surveyId)).map(_ => surveysById[_]);
  const answeredIds = filteredRows.map(_ => Object.keys(_.answers | {})).flat()
  const relatedQuestionIds = uniq([
    ...answeredSurveys.flatMap((survey) => {
      return (
        !isEmpty(queryParams.stepIndices) ? (
          survey?.questionRows?.filter(_ => queryParams.stepIndices.includes((_.stepIndex ?? 0).toString()))
        ) : survey?.questionRows
      )?.map(_ => _.questionId) || [];
    }),
    ...answeredIds,
  ].flat());
  const relatedQuestions = questions.filter(_ => relatedQuestionIds.includes(_.id));
  const _surveyGroups = useCollectionSubscriptionInTenant(surveyGroupsRef);
  const surveyGroups = [...defaultGroups, ..._surveyGroups];
  const groupOptions = surveyGroups.map((_) => ({ label: _.name, value: _.id }));

  const picNameOptions = uniq(rows.map((_) => _.picName)).filter((_) => _).map((_) => ({ label: _, value: _ }));
  const statusOptions = Object.entries(statuses).map(([value, { label }]) => ({ label, value }));
  const areaOptions = uniq(Object.values(areaSetting?.data || {}).map((_) => _.group)).filter((_) => _).map((_) => ({ label: _, value: _ }));

  // アンケート項目の絞り込み用のオプション
  const questionOptions = relatedQuestions.map(_ => ({ label: _.name, value: _.id }));
  
  // 選択された質問の選択肢オプション
  const questionChoiceOptions = useMemo(_ => {
    if (!selectedQuestion || !['checkbox', 'radio', 'select'].includes(selectedQuestion.type)) {
      return [];
    }
    const allAnswerValues = new Set();
    rows.map(row => {
      const answer = row.answers[selectedQuestion.id];
      if (selectedQuestion.type === 'checkbox' && answer) {
        keys(answer).map(key => allAnswerValues.add(key));
      } else if (['radio', 'select'].includes(selectedQuestion.type) && answer) {
        allAnswerValues.add(answer);
      }
    });
    return [...allAnswerValues].map(value => ({ label: value, value }));
  }, [selectedQuestion, rows]);

  const rowsForExport = () => {
    return filteredRows.map((row) => {
      const { id, survey, answers, tag, createdAt, createdBy, adminNote, adminNoteUpdatedAt, adminNoteUpdatedBy } = row;
      return {
        surveyTitle: survey?.title,
        createdAt: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        createdBy: createdBy?.displayName,
        userEmail: createdBy?.email,
        tag,
        adminNote,
        adminNoteUpdatedAt: adminNoteUpdatedAt?.toDate().toLocaleString(),
        adminNoteUpdatedBy: adminNoteUpdatedBy?.displayName,
        ...(
          relatedQuestions.reduce((x, question) => {
            const { id, name, type, } = question;
            const answer = answers[id];
            const value = ({
              checkbox: isPlainObject(answer) ? keys(answer) : answer,
              imageFile: [answer].flat().map(_ => _?.url).join('\n'),
            })[type] || answer;
            return {
              ...x,
              [name]: value,
            };
          }, {})
        ),
      };
    });
  };
  const onClickDateButton = ([startOn, endOn]) => {
    const path = fullPathWithParams(
      { dateRange: [formatDate(startOn, 'yyyy-MM-dd'), formatDate(endOn, 'yyyy-MM-dd')] },
      location
    );
    history.replace(encodeURI(path));
  };
  const openAnswerStatModal = (questionId) => {
    setAnswerStatQuestionId(questionId);
    toggleAnswerStat(true);
  };

  const createAnswerLabelLinkTo = (questionId, answerValue, questionType) => {
    const params = {
      ...queryParams,
      filterQuestionId: [questionId],
      ...(questionType === 'text' && { filterQuestionText: answerValue }),
      ...(questionType === 'imageFile' && { filterQuestionHasImage: answerValue === 'あり' ? '1' : '0' }),
      ...(['checkbox', 'radio', 'select'].includes(questionType) && { filterQuestionValue: [answerValue] }),
    };
    
    return `/admin/surveyAnswers?${qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };

  return (
    <div>
      <div className="admin-survey-answers container-fluid py-5 position-relative">
        <div className="d-flex justify-content-center mb-3">
          <h4>アンケート回答一覧</h4>
        </div>
        {surveyAnswerIdForFilter && (
          <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
            <span>単一の回答で絞り込み中: {surveyAnswerIdForFilter}</span>
            <Button
              color="secondary"
              size="sm"
              onClick={() => {
                const path = fullPathWithParams(
                  { surveyAnswerId: undefined },
                  location
                );
                history.replace(encodeURI(path));
              }}
            >
              クリア
            </Button>
          </div>
        )}
        <div className="d-flex flex-wrap gap-1 align-items-end mb-1" style={{ position: "relative" , zIndex: 4 }}>
          <Button onClick={onClickDateButton.bind(null, [addDays(now, -1), addDays(now, -1)])}>昨日</Button>
          <Button onClick={onClickDateButton.bind(null, [now, now])}>今日</Button>
          <Button onClick={onClickDateButton.bind(null, [startOfMonth(now), endOfMonth(now)])}>今月</Button>
          <Button
            onClick={onClickDateButton.bind(null, [startOfMonth(addMonths(now, -1)), endOfMonth(addMonths(now, -1))])}
          >
            先月
          </Button>
          <QueryDateRangeSelector label="期間" defaultValue={[startOn, endOn]} paramName="dateRange" pickerProps={{ showYearDropdown: true, dropdownMode: 'select' }} />
          <QueryBoolean paramName="showsUserVoice" label="UV表示" defaultValue={'0'} />
        </div>
        <div className="d-flex flex-wrap gap-1 align-items-end mb-2" style={{ position: "relative" , zIndex: 3 }}>
          <div className="d-flex flex-wrap align-items-end gap-2">
            <QuerySelector
              paramName='groups'
              className='ml-0'
              width={400}
              isMulti
              options={groupOptions}
              label='グループで絞込み'
            />
            <QuerySelector
              paramName="surveys"
              width={400}
              isMulti
              options={surveyOptions}
              label="アンケートページで絞込み"
            />
            <QuerySelector
              paramName="stepIndices"
              width={400}
              isMulti
              options={stepOptions}
              label="ステップで絞込み"
            />
          </div>
          <QuerySelector paramName="picNames" width={250} isMulti options={picNameOptions} label="担当で絞込み" />
          <QuerySelector paramName="statuses" width={250} isMulti options={statusOptions} label="状態で絞込み" />
          <QuerySelector paramName="areas" width={250} isMulti options={areaOptions} label="エリア" />
          <QueryBoolean className="d-none" paramName="excludeReinquiry" label="再問合せを除く" defaultValue={'0'} />
          <QuerySelector 
            paramName="filterQuestionId" 
            width={300} 
            options={questionOptions} 
            label="アンケート項目で絞込み" 
            isClearable
          />
          {selectedQuestion && selectedQuestion.type === 'text' && (
            <QueryInput 
              paramName="filterQuestionText" 
              width={250} 
              label="回答内容（部分一致）" 
            />
          )}
          {selectedQuestion && selectedQuestion.type === 'imageFile' && (
            <QueryBoolean 
              paramName="filterQuestionHasImage" 
              label="画像添付あり" 
            />
          )}
          {selectedQuestion && ['checkbox', 'radio', 'select'].includes(selectedQuestion.type) && (
            <QuerySelector 
              paramName="filterQuestionValue" 
              width={300} 
              isMulti={selectedQuestion.type === 'checkbox'}
              options={questionChoiceOptions} 
              label="回答内容" 
              isClearable
            />
          )}
          <SavedSearchParameterForm {...props} type="surveyAnswer" />
        </div>
        <div className="d-flex justify-content-between align-items-end mb-2">
          <div className="d-flex align-items-center gap-3">
            <div>
              {numeral(filteredRows.length).format()} 件
            </div>
            <label className="mb-0">
              <input type="checkbox" className="mr-1" checked={showsOnlyChecked} disabled={isEmpty(checkedAnswers)} onChange={_ => toggleShowsOnlyChecked(_.target.checked)} />
              チェックしたもののみ表示
            </label>
          </div>
          <div className="d-flex gap-2">
            <BulkEmailButton
              recipients={filteredRows.filter(_ => checkedAnswers.includes(_.id)).map(_ => ({ id: _.id, email: _.createdBy?.email, name: _.createdBy?.displayName, uid: _.createdBy?.uid }))}
              sourceType="surveyAnswer"
              onSuccess={() => setCheckedAnswers([])}
            />
            <BulkAdminNoteButton
              targets={filteredRows.filter(_ => checkedAnswers.includes(_.id)).map(_ => ({ id: _.id, ref: _.ref }))}
              onSuccess={() => setCheckedAnswers([])}
            />
            <DeleteSurveyAnswersButton targetAnswers={checkedAnswers.filter(_ => filteredRows.find(row => row.id === _ && ['staff', 'admin'].includes(row.createdBy?.role)))} onFinish={() => setCheckedAnswers([])} />
            <ExportButton fileName='アンケート回答.csv' rows={rowsForExport} hasPersonalInfo user={user} />
          </div>
        </div>
        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {rows.length > 0 ? (
            <table className="table table-bordered">
              <thead className="thead-light text-center position-sticky" style={{ top: 0, zIndex: 1, }}>
                <tr>
                  <th></th>
                  <th style={{ minWidth: 80 }}>状態</th>
                  <th style={{ minWidth: 200 }}>グループ</th>
                  <th style={{ minWidth: 200 }}>アンケートページ</th>
                  <th style={{ minWidth: 120 }}>回答日時</th>
                  <th style={{ minWidth: 120 }}>回答者</th>
                  <th style={{ minWidth: 120 }}>タグ</th>
                  <th style={{ minWidth: 120 }}>担当</th>
                  <th style={{ minWidth: 120 }}>アカウント担当</th>
                  <th style={{ minWidth: 350 }}>メモ</th>
                  {relatedQuestions.map((question) => {
                    const { id, name } = question;
                    return (
                      <th key={id} className="position-relative" style={{ minWidth: 200, maxWidth: 300 }}>
                        {name}
                        <Button color="link" size="sm" className="position-absolute" style={{ top: 2, right: 2 }} onClick={openAnswerStatModal.bind(null, question.id)}>
                          <span className="fas fa-search" />
                        </Button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const { id, ref, survey, answers, tag, adminNote, adminNoteUpdatedAt, adminNoteUpdatedBy, userVoiceDisplayNames, userVoiceHeaders, picName, createdAt, createdBy, status, } = row;
                  const handleCheck = ({ target: { checked }}) => {
                    if (checked) {
                      setCheckedAnswers(array => [...array, id]);
                    } else {
                      setCheckedAnswers(array => array.filter(_ => _ !== id));
                    }
                  }
                  const { label: statusLabel, color } = statuses[status] || {};

                  return (
                    <tr key={id}>
                      <td><input type="checkbox" checked={checkedAnswers.includes(id)} onChange={handleCheck} /></td>
                      <td>
                        <span className={`badge badge-${color || 'secondary'}`}>{statusLabel}</span>
                      </td>
                      <td>{surveyGroups.find(_ => _.id === survey?.surveyGroupId)?.name}</td>
                      <td>{survey?.title}</td>
                      <td>
                        <TenantLink to={`/admin/surveyAnswers/${id}`}>{formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</TenantLink>
                      </td>
                      <td>
                        <TenantLink to={`/admin/users/${createdBy?.uid}`}>{createdBy?.displayName}</TenantLink>
                      </td>
                      <td>{tag}</td>
                      <td>
                        {picName}
                      </td>
                      <td>
                        {row.accountAssignee?.displayName || '設定なし'}
                      </td>
                      <td style={{ whiteSpace: 'pre-line' }}>
                        <div>
                          <EditButton
                            label={false}
                            color="link"
                            size="sm"
                            itemRef={ref}
                            FormModal={ModelFormModal}
                            formProps={{ title: 'メモ', fields: pick(adminFields(), ['adminNote']), }}
                            beforeSubmit={(values) => ({ ...values, adminNoteUpdatedAt: new Date(),adminNoteUpdatedBy: auditData(user) })}
                          />
                        </div>
                        {adminNote}<br />
                        {adminNoteUpdatedAt && adminNoteUpdatedBy && <small className='text-muted'>{adminNoteUpdatedAt.toDate().toLocaleString()} {adminNoteUpdatedBy.displayName}</small>}
                      </td>
                      {relatedQuestions.map((question) => {
                        const { id, type } = question;
                        const answer = answers[id];
                        const surveyAnswerUserVoicesForQuestion = groupedSurveyAnswerUserVoices[`${row.id}__${id}`] || [];
                        // 質問タイプ変更前の文字列回答が残っている場合はそのまま表示する
                        const checkboxText = isPlainObject(answer) ? keys(answer).join(', ') : answer;
                        const defaultDisplayContent = {
                          text: answer,
                          checkbox: checkboxText,
                          radio: answer,
                          select: answer,
                        }[type] || '';
                        const displayValue = {
                          text: answer,
                          checkbox: checkboxText,
                          radio: answer,
                          select: answer,
                          imageFile: [answer].flat().map((answer, i) => (
                            <div key={i}>
                              <a href={answer?.url} target="_blank">
                                {answer?.name}
                              </a>
                            </div>
                          )),
                        }[type];
                        return (
                          <td key={id}>
                            <div className="mb-3">
                              {displayValue}
                            </div>
                            {showsUserVoice && keys(answers || {}).includes(id) && (
                              <div className="flex-wrap gap-1 mb-2">
                                <AddButton
                                  label="UV"
                                  color="link"
                                  size="sm"
                                  itemRef={db.collection('surveyAnswerUserVoices').doc()}
                                  FormModal={ModelFormModal}
                                  formProps={{
                                    title: 'ユーザーズボイス設定',
                                    fields: surveyAnswerUserVoiceFields({
                                      userVoices,
                                      question,
                                      displayNameHint: `元の回答者名: ${createdBy?.displayName}`,
                                    }),
                                    onChange: (prevStatedFields, statedFields) => {
                                      if (prevStatedFields?.userVoiceId?.value !== statedFields?.userVoiceId?.value) {
                                        const selectedUserVoiceId = statedFields.userVoiceId.value;
                                        statedFields.setValues({
                                          displayName: userVoiceDisplayNames?.[selectedUserVoiceId] || '',
                                          userVoiceHeader: userVoiceHeaders?.[selectedUserVoiceId] || '',
                                        });
                                      }
                                    },
                                  }}
                                  initialValues={{
                                    displayContent: defaultDisplayContent,
                                  }}
                                  processValues={async (values) => {
                                    const { displayName, userVoiceHeader, ...restValues } = values;
                                    await ref.update({
                                      userVoiceDisplayNames: {
                                        ...(userVoiceDisplayNames || {}),
                                        [values.userVoiceId]: displayName,
                                      },
                                      userVoiceHeaders: {
                                        ...(userVoiceHeaders || {}),
                                        [values.userVoiceId]: userVoiceHeader,
                                      },
                                    });
                                    return {
                                      ...restValues,
                                      surveyAnswerId: row.id,
                                      questionId: id,
                                      tenantId: tenant.id,
                                    };
                                  }}
                                  onFinish={_ => updateVersion()}
                                />
                                {surveyAnswerUserVoicesForQuestion.map((surveyAnswerUserVoice) => (
                                  <div key={surveyAnswerUserVoice.id} className="d-flex align-items-center gap-1">
                                    <span className="badge badge-secondary">{userVoicesById[surveyAnswerUserVoice.userVoiceId]?.name}</span>
                                    <EditButton
                                      label={false}
                                      color="link"
                                      size="sm"
                                      className="p-0"
                                      itemRef={surveyAnswerUserVoice.ref}
                                      FormModal={ModelFormModal}
                                      formProps={{
                                        title: 'ユーザーズボイス設定',
                                        fields: surveyAnswerUserVoiceFields({
                                          userVoices,
                                          question,
                                          displayNameHint: `元の回答者名: ${createdBy?.displayName}`,
                                        }),
                                        onChange: (prevStatedFields, statedFields) => {
                                          if (prevStatedFields?.userVoiceId?.value !== statedFields?.userVoiceId?.value) {
                                            const selectedUserVoiceId = statedFields.userVoiceId.value;
                                            statedFields.setValues({
                                              displayName: userVoiceDisplayNames?.[selectedUserVoiceId] || '',
                                              userVoiceHeader: userVoiceHeaders?.[selectedUserVoiceId] || '',
                                            });
                                          }
                                        },
                                      }}
                                      beforeEdit={(item) => ({
                                        ...item,
                                        displayName: userVoiceDisplayNames?.[item.userVoiceId],
                                        userVoiceHeader: userVoiceHeaders?.[item.userVoiceId],
                                      })}
                                      beforeSubmit={async (values) => {
                                        const { displayName, userVoiceHeader, ...restValues } = values;
                                        await ref.update({
                                          userVoiceDisplayNames: {
                                            ...(userVoiceDisplayNames || {}),
                                            [values.userVoiceId]: displayName,
                                          },
                                          userVoiceHeaders: {
                                            ...(userVoiceHeaders || {}),
                                            [values.userVoiceId]: userVoiceHeader,
                                          },
                                        });
                                        return {
                                          ...restValues,
                                          surveyAnswerId: row.id,
                                          questionId: id,
                                          tenantId: tenant.id,
                                        };
                                      }}
                                      onFinish={_ => updateVersion()}
                                    />
                                    <DeleteButton
                                      label={false}
                                      color="link"
                                      size="sm"
                                      className="p-0"
                                      itemRef={surveyAnswerUserVoice.ref}
                                      onFinish={_ => updateVersion()}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div>No Data</div>
          )}
        </div>
      </div>
      {showsAnswerStat && <AnswerStatModal isOpen onClickClose={toggleAnswerStat.bind(null, false)} defaultQuestionId={answerStatQuestionId} questions={questions} rows={filteredRows} answerLabelLinkTo={createAnswerLabelLinkTo} />}
    </div>
  );
});
